import * as React from 'react';
import { useLocalization, useLocalizationExtension } from '../../context/Localization/localization';
import { IOptionData, OptionList } from './OptionList/OptionList';
import { Spinner } from '../Spinner/Spinner';
import { AccordionSection } from './AccordionSection/AccordionSection';
import { useField } from 'formik';
import { AccordionDuration } from './Accordion/Accordion';

const BaseDynamicAccordionMaxFields = 8;

interface IDynamicAccordionSectionProps<T> {
    /**
     * Think of this as the dependency list for getValues.
     *  This lets React know that two sections with the same name but different keys should be rendered independently.
     */
    key: React.Key;
    /** The label to display for this section */
    label: string;
    /** The field name represending the selected value */
    name: string;
    /** Whether the section should be expanded */
    expanded: boolean;
    /** Whether the section should display an error */
    error: boolean;
    /** Called when the value should be cleared */
    onClear: () => void;
    /** Called when the section should retrieve values */
    getValues: () => Promise<Array<IOptionData<T>>>;
    /** Called to format the selected value for display */
    formatDisplayValue?: (selected: T) => string;
    /** If `true`, the accordion will be scrolled to when the content loads */
    scrollTo?: boolean;
    /** Number of fields to render before displaying the "other" option */
    maxFields?: number;
    /** If it should display the loading spinner due to the actions of the parent component */
    parentLoading?: boolean;
    /** If `true`, option button labels render without the theme button-font `text-transform` */
    preserveCase?: boolean;
}

/**
 * Accordion section that will load it's data on demand.
 */
const DynamicAccordionSectionInstance = <T extends string | number | {} | undefined>(props: IDynamicAccordionSectionProps<T>) => {
    const [loading, setLoading] = React.useState(false);
    const [values, setValues] = React.useState<Array<IOptionData<T>>>();
    const Localization = useLocalization();
    const { formatRequired } = useLocalizationExtension();
    const [error, setError] = React.useState(false);

    const [{ value }] = useField(props.name);
    const displayValue = props.formatDisplayValue ? props.formatDisplayValue(value) : value;

    const { expanded, getValues } = props;

    const [scrollTo, setScrollTo] = React.useState<boolean>(!!props.scrollTo && !!values);

    React.useEffect(() => {
        if (!loading && expanded && !values && !error) {
            setLoading(true);
            getValues()
                .then(
                    (response) => {
                        setValues(response || []);
                    },
                    () => setError(true),
                )
                .finally(() => setLoading(false));
        }
    }, [error, getValues, loading, expanded, values]);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setScrollTo(!!props.scrollTo && !!values);
        }, AccordionDuration);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [props.scrollTo, values]);

    return (
        <AccordionSection
            headingLabel={props.label + (displayValue && displayValue.length > 0 ? ' - ' + displayValue : '')}
            expanded={props.expanded}
            id={props.name}
            ariaControls={props.name}
            onClick={props.onClear}
            error={props.error}
            errorLabel={formatRequired(props.label)}
            scroll={scrollTo}
        >
            {props.parentLoading || loading ? (
                <Spinner />
            ) : (
                <OptionList
                    name={props.name}
                    data={values}
                    limitFieldsOptions={{ maxFields: props.maxFields || BaseDynamicAccordionMaxFields,
                        showMoreText: Localization.Other }}
                    preserveCase={props.preserveCase}
                />
            )}
        </AccordionSection>
    );
};

export const DynamicAccordionSection = React.memo(DynamicAccordionSectionInstance) as typeof DynamicAccordionSectionInstance;
